/**
 * Локализация интерфейса.
 *
 * Язык по умолчанию — английский: игра про всемирную историю, и первым её
 * открывают не только русскоязычные. Выбор запоминается в браузере, а у
 * вошедшего пользователя — ещё и в профиле, чтобы он совпадал на всех
 * устройствах и чтобы письма приходили на понятном языке.
 *
 * Русские правила множественного числа отданы i18next: у них три формы
 * («1 раунд», «2 раунда», «5 раундов»), и писать это вручную — заведомая
 * ошибка в одной из форм.
 *
 * Английский словарь вшит в основной бандл, остальные подгружаются отдельным
 * файлом: язык по умолчанию английский, и большинство первых заходов не должно
 * платить трафиком за словари, которые им не понадобятся.
 */
import i18next from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./en.json";

export const LANGUAGES = ["en", "ru"] as const;
export type Language = (typeof LANGUAGES)[number];

export const LANGUAGE_NAMES: Record<Language, string> = {
  en: "English",
  ru: "Русский",
};

const STORAGE_KEY = "chronoguessr:lang";

export function isLanguage(value: unknown): value is Language {
  return typeof value === "string" && (LANGUAGES as readonly string[]).includes(value);
}

/** Запомненный выбор. Приватный режим и запрет на хранилище не должны ломать сайт. */
export function storedLanguage(): Language | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isLanguage(saved) ? saved : null;
  } catch {
    return null;
  }
}

export function rememberLanguage(language: Language) {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Ничего страшного: язык проживёт до конца вкладки.
  }
}

/**
 * Меняет язык интерфейса. Атрибут lang нужен не для красоты: по нему браузер
 * выбирает правила переносов и словарь проверки орфографии в полях ввода.
 */
export function setLanguage(language: Language) {
  rememberLanguage(language);
  document.documentElement.lang = language;

  void loadBundle(language)
    .then(() => i18next.changeLanguage(language))
    .catch(() => {
      // Словарь не доехал: язык интерфейса не меняется, выбор всё равно
      // запомнен и применится при следующем заходе.
    });
}

export function currentLanguage(): Language {
  return isLanguage(i18next.resolvedLanguage) ? i18next.resolvedLanguage : "en";
}

/** Уже загруженные словари: повторно тянуть файл незачем. */
const loaded = new Set<Language>(["en"]);

/** Подгружает словарь языка, если он ещё не в памяти. */
async function loadBundle(language: Language): Promise<void> {
  if (loaded.has(language)) return;

  const bundle = await import(`./${language}.json`);
  i18next.addResourceBundle(language, "translation", bundle.default, true, true);
  loaded.add(language);
}

void i18next.use(initReactI18next).init({
  resources: { en: { translation: en } },
  // Первый заход — английский, даже если браузер русский: так решено для
  // домена. Язык браузера не угадываем, его выбирает сам человек.
  lng: "en",
  fallbackLng: "en",
  interpolation: { escapeValue: false },
});

/**
 * Готовит выбранный язык до первого рендера.
 *
 * Если запомненный язык не английский, его словарь нужно дождаться: иначе
 * страница успеет мигнуть английским и перерисоваться.
 */
export async function initLanguage(): Promise<void> {
  const saved = storedLanguage();
  if (!saved || saved === "en") {
    document.documentElement.lang = "en";
    return;
  }

  try {
    await loadBundle(saved);
    await i18next.changeLanguage(saved);
  } catch {
    // Не загрузился словарь — интерфейс остаётся английским. Это хуже, чем
    // хотел человек, но лучше, чем пустой экран.
  }
  document.documentElement.lang = currentLanguage();
}

export default i18next;
